import {Utility, GeoLocation, GeoCoordinate} from "@reapptor-apps/reapptor-toolkit";
import {PageRoute} from "@reapptor-apps/reapptor-react-common";
import {AddressHelper, BaseComparator, SelectListItem, StatusListItem} from "@reapptor-apps/reapptor-react-components";
import OrganizationContract from "@/models/server/OrganizationContract";
import User from "../models/server/User";
import ServicePoint from "@/models/server/bout/ServicePoint";
import Waypoint from "@/models/server/bout/Waypoint";
import BoatPrice from "@/models/server/bout/BoatPrice";
import Country from "@/models/server/bout/Country";
import Area from "@/models/server/bout/Area";

export default class Comparator {

    private static isEqualGeoCoordinate(x: GeoCoordinate, y: GeoCoordinate): boolean {
        return (x.lat === y.lat) && (x.lon === y.lon);
    }

    private static isEqualGeoLocation(x: GeoLocation, y: GeoLocation): boolean {
        if (x.formattedAddress === y.formattedAddress) {
            return true;
        }
        
        const xCoordinate: GeoCoordinate | null = AddressHelper.getCoordinate(x);
        const yCoordinate: GeoCoordinate | null = AddressHelper.getCoordinate(y);
        
        if (xCoordinate && yCoordinate) {
            return this.isEqualGeoCoordinate(xCoordinate, yCoordinate);
        }
        
        const xAddress: string = AddressHelper.removeLatLon(x.formattedAddress);
        const yAddress: string = AddressHelper.removeLatLon(y.formattedAddress);
        
        return (xAddress === yAddress);
    }

    private static isEqualStatusListItem(x: StatusListItem, y: StatusListItem): boolean {
        return (x.completed === y.completed) && (x.lineThrough === y.lineThrough) && (x.value === y.value);
    }
    
    public static isEqualPageRoute(x: PageRoute | null, y: PageRoute | null): boolean {

        if (x === y) {
            return true;
        }
        if ((x == null) && (y == null)) {
            return true;
        }
        if ((x == null) || (y == null)) {
            return false;
        }
        if (x.name !== y.name) {
            return false;
        }
        if (x.index !== y.index) {
            return false;
        }
        if (x.id !== y.id) {
            return false;
        }
        
        // TODO: compare parameters
        
        return true;
    }
    
    public static isEqual(x: any | string | null | undefined, y: any | string | null | undefined): boolean {
        
        if (x === y) {
            return true;
        }

        if ((x == null) && (y == null)) {
            return true;
        }

        if ((x == null) || (y == null)) {
            return false;
        }
        
        if (typeof x === "object") {

            if ((x instanceof StatusListItem) || (x.isStatusListItem)) {
                return ((y instanceof StatusListItem) || (y.isStatusListItem)) && (this.isEqualStatusListItem(x, y));
            }

            if ((x instanceof SelectListItem) || (x.isSelectListItem)) {
                return ((y instanceof SelectListItem) || (y.isSelectListItem)) && ((x as SelectListItem).value === (y as SelectListItem).value);
            }
            
            if ((x instanceof PageRoute) || (x.isPageRoute)) {
                return ((y instanceof PageRoute) || (y.isPageRoute)) && (this.isEqualPageRoute(x, y));
            }

            if ((x instanceof OrganizationContract) || (x.isOrganizationContract)) {
                return ((y instanceof OrganizationContract) || (y.isOrganizationContract))
                    ? ((x as OrganizationContract).id == (y as OrganizationContract).id)
                    : ((typeof y === "string") && ((x as OrganizationContract).id === y as string))
            }

            if ((x instanceof User) || (x.isUser)) {
                return ((y instanceof User) || (y.isUser))
                    ? ((x as User).id == (y as User).id)
                    : ((typeof y === "string") && ((x as User).id === y as string));
            }
            
            if ((x instanceof Country) || (x.isCountry)) {
                return ((y instanceof Country) || (y.isCountry))
                    ? ((x as Country).id == (y as Country).id)
                    : ((typeof y === "string") && (((x as Country).id === y as string) || ((x as Country).code === y as string)));
            }

            if ((x instanceof ServicePoint) || (x.isServicePoint)) {
                return ((y instanceof ServicePoint) || (y.isServicePoint))
                    ? ((x as ServicePoint).id == (y as ServicePoint).id)
                    : ((typeof y === "string") && ((x as ServicePoint).id === y as string));
            }

            if ((x instanceof Waypoint) || (x.isWaypoint)) {
                return ((y instanceof Waypoint) || (y.isWaypoint))
                    ? ((x as Waypoint).id == (y as Waypoint).id)
                    : ((typeof y === "string") && ((x as Waypoint).id === y as string));
            }

            if ((x instanceof BoatPrice) || (x.isBoatPrice)) {
                return ((y instanceof BoatPrice) || (y.isBoatPrice))
                    ? ((x as BoatPrice).id == (y as BoatPrice).id)
                    : ((typeof y === "string") && ((x as BoatPrice).id === y as string));
            }

            if ((x instanceof Area) || (x.isArea)) {
                return ((y instanceof Area) || (y.isArea))
                    ? ((x as Area).id == (y as Area).id)
                    : ((typeof y === "string") && ((x as Area).id === y as string));
            }

            if ((x instanceof GeoLocation) || (x.isGeoLocation)) {
                return ((y instanceof GeoLocation) || (y.isGeoLocation)) && (this.isEqualGeoLocation(x, y));
            }

            if ((x instanceof GeoCoordinate) || (x.isGeoCoordinate)) {
                return ((y instanceof GeoCoordinate) || (y.isGeoCoordinate)) && (this.isEqualGeoCoordinate(x, y));
            }

            if ((Utility.isDateType(x)) || (Utility.isDateType(y))) {
                const xDate: Date = new Date(x);
                const yDate: Date = new Date(y);
                return (xDate.valueOf() === yDate.valueOf());
            }
        }
        
        if ((typeof x === "string") && (typeof y === "object")) {
            return Comparator.isEqual(y as any, x as string);
        }
        
        if ((Array.isArray(x)) && (Array.isArray(y))) {
            
            if (x.length !== y.length) {
                return false;
            }
            
            for (let i: number = 0; i < x.length; i++) {
                if (!Comparator.isEqual(x[i], y[i])) {
                    return false;
                }
            }
            
            return true;
        }

        return (x == y);
    }
    
    public static initialize(): void {
        BaseComparator.register(this.isEqual);
    }
}